import {
  type PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import type { ThemeMode } from '@store/appConfig/types';

import { Fade } from '@mui/material';

import { fetchUserConfig } from '@features/auth/store/thunks/fetchUserConfig';
import { POST_MESSAGE_EVENT_TYPE } from '@modules/types';
import { setAppConfig } from '@store/appConfig/appConfigSlice';
import { isValidJSON } from '@utils/isValidJson';
import { parseSearchParameters } from '@utils/parseUrlParameters';
import { sendPostMessage } from '@utils/sendPostMessage';

import { Loader } from '@components';
import { ENV } from '@constants';
import { useAppDispatch, useInitializeTimezone } from '@hooks';

const AuthProvider = ({ children }: PropsWithChildren) => {
  const dispatch = useAppDispatch();

  const isIframe = window.self !== window.top;
  const isProduction = ENV.MODE === 'production';
  const [postMessageToken, setPostMessageToken] = useState('');
  const appliedCredentialsTokenReference = useRef('');

  const queryParameterToken = parseSearchParameters(
    window.location.search,
    'token'
  );
  const theme = (parseSearchParameters(
    window.location.search,
    'themeMode'
  )?.toLowerCase() ?? 'light') as ThemeMode;

  const postMessageWorker = useCallback(
    ({ data }: { data: unknown }) => {
      if (!isValidJSON(data)) {
        return;
      }

      const { type, token } = JSON.parse(data as string);

      if (type !== POST_MESSAGE_EVENT_TYPE.SET_CREDENTIALS || !token) {
        return;
      }

      if (token === appliedCredentialsTokenReference.current) {
        return;
      }

      appliedCredentialsTokenReference.current = token;
      setPostMessageToken(token);
      dispatch(fetchUserConfig(token));

      sendPostMessage({ type: POST_MESSAGE_EVENT_TYPE.CREDENTIALS_RECEIVED });
    },
    [dispatch]
  );

  useEffect(() => {
    if (isIframe) {
      window.addEventListener('message', postMessageWorker);
    }

    return () => {
      if (isIframe) {
        window.removeEventListener('message', postMessageWorker);
      }
    };
  }, [postMessageWorker, isIframe]);

  // Description: outside of production, iframe-mode works locally without a real
  // parent frame sending SET_CREDENTIALS - it falls back to the same default JWT
  // token used for non-iframe local development (#hardcodedJWTtoken).
  const iframeFallbackToken = isProduction ? '' : ENV.VITE_DEFAULT_JWT_TOKEN;

  const token = isIframe
    ? postMessageToken || iframeFallbackToken
    : (queryParameterToken ?? ENV.VITE_DEFAULT_JWT_TOKEN ?? '');

  useEffect(() => {
    if (!isIframe || !iframeFallbackToken) {
      return;
    }

    dispatch(fetchUserConfig(iframeFallbackToken));
  }, [dispatch, isIframe, iframeFallbackToken]);

  useEffect(() => {
    if (isIframe) {
      return;
    }

    const initialToken =
      queryParameterToken ?? ENV.VITE_DEFAULT_JWT_TOKEN ?? '';

    if (initialToken) {
      dispatch(fetchUserConfig(initialToken));
    }
  }, [dispatch, isIframe, queryParameterToken]);

  useEffect(() => {
    dispatch(
      setAppConfig({
        mode: theme,
      })
    );
  }, [dispatch, theme]);

  useInitializeTimezone(token);

  return isIframe && !token ? (
    <Loader />
  ) : (
    <Fade in mountOnEnter unmountOnExit timeout={2000}>
      <div>{children}</div>
    </Fade>
  );
};

export default AuthProvider;
